import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, ChevronRight, Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SupportAccessSection } from '@/components/settings/SupportAccessSection'
import { useOrgLifecycleStore } from '@/stores/org-lifecycle-store'
import { useAuthStore } from '@/stores/auth-store'
import { useSettingsStore } from '@/stores/settings-store'
import { toastApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

// ===================
// Helpers
// ===================

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.ceil(seconds / 60)
  if (minutes < 60) return `~${minutes} minute${minutes === 1 ? '' : 's'}`
  const hours = Math.ceil(minutes / 60)
  return `~${hours} hour${hours === 1 ? '' : 's'}`
}

function formatExpiryCountdown(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now()
  if (diff <= 0) return 'Expired'
  const hours = Math.floor(diff / (1000 * 60 * 60))
  if (hours > 24) return `Expires in ${Math.ceil(hours / 24)} days`
  if (hours > 0) return `Expires in ${hours} hour${hours === 1 ? '' : 's'}`
  const minutes = Math.floor(diff / (1000 * 60))
  return `Expires in ${minutes} minute${minutes === 1 ? '' : 's'}`
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const minutes = Math.floor(diff / (1000 * 60))
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

// ===================
// Component
// ===================

export function DataPrivacyTab() {
  const user = useAuthStore((s) => s.user)
  const role = useAuthStore((s) => s.role)
  const organization = useSettingsStore((s) => s.organization)
  const orgName = organization?.profile?.companyName ?? 'Your organisation'
  const isAdmin = role === 'admin'

  const {
    deletionRequest,
    activeSessions,
    sizeEstimate,
    currentExport,
    recentExports,
    isLoading,
    loadStatus,
    loadSizeEstimate,
    loadExports,
    loadActiveSessions,
    scheduleDeletion,
    cancelDeletion,
    startExport,
    stopPolling,
  } = useOrgLifecycleStore()

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [confirmName, setConfirmName] = useState('')

  // Load everything on mount
  useEffect(() => {
    void loadStatus()
    void loadSizeEstimate()
    void loadExports()
    return () => {
      stopPolling()
    }
  }, [loadStatus, loadSizeEstimate, loadExports, stopPolling])

  // Load active sessions when opening the delete modal
  useEffect(() => {
    if (isDeleteModalOpen) {
      void loadActiveSessions()
    }
  }, [isDeleteModalOpen, loadActiveSessions])

  const handleStartExport = useCallback(async () => {
    try {
      await startExport()
      toast.success('Export started — you will be notified when it is ready')
    } catch (err) {
      toastApiError(err, 'Failed to start export')
    }
  }, [startExport])

  const handleCancelDeletion = useCallback(async () => {
    try {
      await cancelDeletion()
      toast.success('Account deletion cancelled')
    } catch (err) {
      toastApiError(err, 'Failed to cancel deletion')
    }
  }, [cancelDeletion])

  const handleScheduleDeletion = useCallback(async () => {
    try {
      await scheduleDeletion(confirmName, orgName)
      setIsDeleteModalOpen(false)
      setConfirmName('')
      toast.success('Account deletion scheduled — all admins have been notified')
    } catch (err) {
      toastApiError(err, 'Failed to schedule deletion')
    }
  }, [confirmName, orgName, scheduleDeletion])

  const exportInProgress =
    currentExport?.status === 'pending' || currentExport?.status === 'running'
  const canStartExport =
    isAdmin &&
    !exportInProgress &&
    !sizeEstimate?.exceedsLimit &&
    !recentExports.some(
      (e) =>
        e.status === 'completed' &&
        Date.now() - new Date(e.requestedAt).getTime() < 24 * 60 * 60 * 1000,
    )

  const nextExportAvailableAt = (() => {
    const latestCompleted = recentExports.find((e) => e.status === 'completed')
    if (!latestCompleted) return null
    const unblocked = new Date(latestCompleted.requestedAt).getTime() + 24 * 60 * 60 * 1000
    return unblocked > Date.now() ? new Date(unblocked) : null
  })()

  return (
    <div className="space-y-10">
      {/* ========================================
          Export Organisation Data
          ======================================== */}
      <section className="space-y-4">
        <div>
          <h2 className="text-sm font-medium">Export Organisation Data</h2>
          <p className="text-meta mt-1">
            Download a complete copy of your organisation's data (bids, documents, plans, drafts).
            Chat conversations with the AI assistant are excluded — individual users can request
            their own chat history via support.
          </p>
        </div>

        <div className="rounded-xl border border-border bg-card shadow-sm p-4 space-y-3">
          {sizeEstimate ? (
            <div className="text-meta space-y-1">
              <div>
                Estimated size:{' '}
                <span className="font-medium text-foreground">
                  {formatBytes(sizeEstimate.totalBytes)}
                </span>{' '}
                ({sizeEstimate.fileCount} files)
              </div>
              <div>
                Estimated time:{' '}
                <span className="font-medium text-foreground">
                  {formatDuration(sizeEstimate.estimatedDurationSeconds)}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-meta">
              <Loader2 className="inline h-3 w-3 mr-1 animate-spin" /> Calculating size…
            </div>
          )}

          {sizeEstimate?.exceedsLimit && (
            <div className="rounded-lg bg-destructive/5 border border-destructive/20 p-3 text-xs text-destructive">
              Your data exceeds the 15 GB automated export limit. Contact{' '}
              <a href="mailto:hello@bidcontext.ai" className="underline">
                hello@bidcontext.ai
              </a>{' '}
              for a manual export.
            </div>
          )}

          <div>
            <Button
              onClick={() => void handleStartExport()}
              disabled={!canStartExport || isLoading}
              className="gap-2"
            >
              {exportInProgress ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating export…
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Generate Export
                </>
              )}
            </Button>
            {!isAdmin && (
              <p className="text-meta mt-2">
                Only organisation admins can generate exports.
              </p>
            )}
            {nextExportAvailableAt && !exportInProgress && (
              <p className="text-meta mt-2">
                Next export available at {nextExportAvailableAt.toLocaleString()}
              </p>
            )}
          </div>
        </div>

        {/* Recent exports list */}
        {recentExports.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-medium text-muted-foreground">Recent exports</h3>
            <div className="rounded-xl border border-border bg-card shadow-sm divide-y divide-border">
              {recentExports.map((job) => (
                <div key={job.id} className="p-3 flex items-center justify-between text-xs">
                  <div className="space-y-0.5">
                    <div className="font-medium">
                      {job.status === 'completed' && job.actualSizeBytes
                        ? `Export (${formatBytes(job.actualSizeBytes)})`
                        : `Export — ${job.status}`}
                    </div>
                    <div className="text-muted-foreground">
                      Generated {formatRelativeTime(job.requestedAt)}
                      {job.status === 'completed' && job.downloadExpiresAt && (
                        <> · {formatExpiryCountdown(job.downloadExpiresAt)}</>
                      )}
                    </div>
                  </div>
                  {job.status === 'completed' && job.downloadUrl &&
                    job.downloadExpiresAt &&
                    new Date(job.downloadExpiresAt) > new Date() && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(job.downloadUrl!, '_blank')}
                      >
                        Download
                      </Button>
                    )}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ========================================
          Data Retention (informational)
          ======================================== */}
      <section className="space-y-2">
        <h2 className="text-sm font-medium">Data Retention</h2>
        <div className="rounded-xl border border-border bg-card shadow-sm p-4 text-meta space-y-2">
          <p>
            When you delete your organisation, all data is permanently erased after a 7-day grace
            period. During the grace period, any admin can cancel the deletion.
          </p>
          <p>
            Automated database backups and S3 object versioning retain data for up to an additional
            7 days after deletion before being permanently purged.
          </p>
          <p>
            <a
              href="/privacy"
              className="inline-flex items-center gap-0.5 underline hover:text-foreground"
              target="_blank"
              rel="noreferrer"
            >
              Read the full privacy policy
              <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
            </a>
          </p>
        </div>
      </section>

      {/* ========================================
          Support Access (org admins only — the API refuses everyone else)
          ======================================== */}
      {isAdmin && <SupportAccessSection />}

      {/* ========================================
          Danger Zone
          ======================================== */}
      <section className="space-y-2">
        <h2 className="text-sm font-medium">Danger Zone</h2>

        {deletionRequest ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium">
                  This organisation will be deleted on{' '}
                  {new Date(deletionRequest.scheduledFor).toLocaleString()}
                </p>
                <p className="text-meta">
                  Scheduled by {deletionRequest.requestedByEmail}. Any admin can cancel this
                  deletion during the grace period.
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => void handleCancelDeletion()}
              disabled={!isAdmin || isLoading}
            >
              Cancel Deletion
            </Button>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card shadow-sm p-4 space-y-3">
            <div className="space-y-1">
              <p className="text-sm font-medium">Delete Organisation</p>
              <p className="text-meta">
                Permanently delete {orgName} and all its data. You will have 7 days to cancel.
                All admins will be notified by email.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(true)}
              disabled={!isAdmin}
              className={cn('bg-background hover:text-destructive', !isAdmin && 'cursor-not-allowed')}
            >
              Delete Organisation
            </Button>
            {!isAdmin && (
              <p className="text-meta">
                Only organisation admins can delete the account.
              </p>
            )}
          </div>
        )}
      </section>

      {/* ========================================
          Confirmation modal
          ======================================== */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              Delete {orgName}?
            </DialogTitle>
            <DialogDescription>
              This will permanently delete all your organisation's data after a 7-day grace period.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border border-border p-3 text-xs space-y-1">
              <p className="font-medium">This will delete:</p>
              <ul className="list-disc pl-5 text-muted-foreground space-y-0.5">
                {sizeEstimate && (
                  <>
                    <li>{sizeEstimate.tableRowCounts['bids'] ?? 0} bids</li>
                    <li>{sizeEstimate.tableRowCounts['documents'] ?? 0} documents</li>
                    <li>{sizeEstimate.tableRowCounts['users'] ?? 0} user accounts</li>
                  </>
                )}
              </ul>
            </div>

            {activeSessions && activeSessions.totalActive > 0 && (
              <div className="rounded-lg bg-destructive/5 border border-destructive/20 p-3 text-xs">
                <p className="font-medium text-destructive">
                  ⚠ {activeSessions.totalActive} active job
                  {activeSessions.totalActive === 1 ? '' : 's'} running
                </p>
                <p className="text-muted-foreground mt-1">
                  In-flight work will be cancelled and their results lost.
                </p>
              </div>
            )}

            <div className="rounded-lg bg-muted/40 border border-border p-3 text-xs">
              <p className="text-muted-foreground">
                💡 <strong>Export your data first</strong> to keep a copy before deleting. Cancel
                this dialog and click "Generate Export" above.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-name" className="text-xs">
                Type <span className="font-mono font-semibold">{orgName}</span> to confirm:
              </Label>
              <Input
                id="confirm-name"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={orgName}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setIsDeleteModalOpen(false)
                setConfirmName('')
              }}
            >
              Cancel
            </Button>
            <Button
              variant="ghost"
              className="hover:text-destructive"
              onClick={() => void handleScheduleDeletion()}
              disabled={confirmName !== orgName || isLoading}
            >
              Schedule Deletion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unused variable suppression */}
      {user && null}
    </div>
  )
}

